import { AddComment, Star, View as ViewIcon } from '@carbon/icons-react';
import { HorizBox, Pad, VertBox, WindowTitle } from 'component/basics';
import { CTAButton, IconButton } from 'component/button';
import { colorSurfacePrimary } from 'component/color';
import { FormField, SearchField } from 'component/form';
import { useGlobalQuestions } from 'component/question';
import { Heading, TextField, UtilityText } from 'component/text';
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import type { EditorialDashConfig } from 'structure/editorial-dash';
import { useGlobalTopics } from 'structure/topic';
import { Catcher } from 'system/catcher';
import type { Datastore } from 'util/datastore';
import { useDatastore, useSessionData } from 'util/datastore';
import { useConfig } from 'util/features';
import type { Feature, TLabel } from 'util/stdtypes';
import { computedLabel, t } from 'util/stdtypes';
import { toTitleCase } from 'util/util';

export type InstancePreview = {
    key: string;
    name: string;
    from: string;
    time: number;
};

export type NewInstance = {
    name: string;
};

export type InstanceFilter = {
    component: React.ComponentType<{ structureKey: string; instanceList: InstancePreview[] }>;
    filter: (datastore: Datastore, instance: InstancePreview) => boolean;
};

export type InstanceCreator = {
    structureKey: string;
    creator: (datastore: Datastore, instance: NewInstance) => Promise<{ key: string }>;
};

export type NewInstancePostCreationTriggerProps = {
    datastore: Datastore;
    instance: NewInstance;
    structureKey: string;
    instanceKey: string;
};

export type NewInstancePostCreationTriggerFunc = (props: NewInstancePostCreationTriggerProps) => Promise<void>;

//Note: I added instanceKey as fallback in case some older widgets don't support instancePreview
export type InstanceManagerConfig = {
    instanceTopLeftWidgets: React.ComponentType<{ instance: InstancePreview; instanceKey: string; structureKey: string }>[];
    instanceTopRightWidgets: React.ComponentType<{ instance: InstancePreview; instanceKey: string; structureKey: string }>[];
    instanceBodyWidgets: React.ComponentType<{ instance: InstancePreview; instanceKey: string; structureKey: string }>[];
    instanceBottomLeftWidgets: React.ComponentType<{ instance: InstancePreview; instanceKey: string; structureKey: string }>[];
    instanceBottomRightWidgets: React.ComponentType<{ instance: InstancePreview; instanceKey: string; structureKey: string }>[];
    instanceFilters: InstanceFilter[];
    newInstanceWidgets: React.ComponentType<{
        structureKey: string;
        instance: NewInstance;
        setInstance: (instance: NewInstance) => void;
    }>[];
    newInstanceCreateBlockers: ((datastore: Datastore, instance: NewInstance) => boolean)[];
    newInstanceCreators: InstanceCreator[];
    newInstancePostCreateTriggers: NewInstancePostCreationTriggerFunc[];
};
export const InstanceManagerFeature: Feature<EditorialDashConfig, InstanceManagerConfig> = {
    key: 'question-topic-manager',
    name: 'Question/Topic Manager',
    dev: true,
    subscreens: {
        newInstance: NewInstanceScreen,
    },
    providesConfig: {
        dashboardTabs: [
            {
                key: 'questions',
                label: t('Questions'),
                icon: AddComment,
                component: QuestionManager,
            },
            {
                key: 'topics',
                label: t('Topics'),
                icon: Star,
                component: TopicManager,
            },
        ],
    },
    receivesConfigDefaults: {
        instanceTopLeftWidgets: [],
        instanceTopRightWidgets: [ViewAction],
        instanceBodyWidgets: [],
        instanceBottomLeftWidgets: [],
        instanceBottomRightWidgets: [],
        instanceFilters: [
            {
                component: Search,
                filter: searchFilter,
            },
        ],
        newInstanceWidgets: [],
        newInstanceCreateBlockers: [],
        newInstanceCreators: [
            {
                structureKey: 'question',
                creator: async (datastore: Datastore, newInstance: NewInstance) => {
                    const { questionKey } = await datastore.callServerAsync('questions', 'add', { newInstance });
                    return { key: questionKey };
                },
            },
            {
                structureKey: 'topic',
                creator: async (datastore: Datastore, newInstance: NewInstance) => {
                    const topicKey = await datastore.callServerAsync('topics', 'add', { topic: newInstance });
                    return { key: topicKey };
                },
            },
        ],
        newInstancePostCreateTriggers: [],
    },
};

export type SearchProps = { structureKey: string };

export function Search({ structureKey }: SearchProps) {
    const datastore = useDatastore();
    const search = useSessionData('search');
    function setSearch(search) {
        datastore.setSessionData('search', search);
    }
    return <SearchField onChange={setSearch} placeholder={'Search for a ' + structureKey + '...'} testID='search' value={search} />;
}

export function searchFilter(datastore: Datastore, instance: InstancePreview) {
    const search = datastore.getSessionData('search') ?? '';
    return instance.name?.toLowerCase().includes(search.toLowerCase()) ?? false;
}

export type ViewActionProps = { structureKey: string; instance: InstancePreview };

export function ViewAction({ structureKey, instance }: ViewActionProps) {
    const datastore = useDatastore();
    function onPress() {
        datastore.gotoInstance({ structureKey, instanceKey: instance.key });
    }
    return <IconButton icon={ViewIcon} onPress={onPress} testID='view' />;
}

function InstanceCard({ instance, structureKey }) {
    const s = InstanceCardStyle;
    const {
        instanceTopLeftWidgets = [],
        instanceTopRightWidgets = [],
        instanceBodyWidgets = [],
        instanceBottomLeftWidgets = [],
        instanceBottomRightWidgets = [],
    } = useConfig<InstanceManagerConfig>();

    return (
        <View style={s.card}>
            <HorizBox spread>
                <HorizBox>
                    {instanceTopLeftWidgets.map((widget, index) =>
                        React.createElement(widget, { instance, structureKey, key: index, instanceKey: instance.key }),
                    )}
                </HorizBox>
                <HorizBox>
                    {instanceTopRightWidgets.map((widget, index) =>
                        React.createElement(widget, { instance, structureKey, key: index, instanceKey: instance.key }),
                    )}
                </HorizBox>
            </HorizBox>
            <UtilityText text={instance.name} type='small' weight='medium' />
            <VertBox>
                {instanceBodyWidgets.map((widget, index) =>
                    React.createElement(widget, { instance, structureKey, key: index, instanceKey: instance.key }),
                )}
            </VertBox>
            <HorizBox spread>
                <HorizBox>
                    {instanceBottomLeftWidgets.map((widget, index) =>
                        React.createElement(widget, { instance, instanceKey: instance.key, structureKey, key: index }),
                    )}
                </HorizBox>
                <HorizBox>
                    {instanceBottomRightWidgets.map((widget, index) =>
                        React.createElement(widget, { instance, structureKey, key: index, instanceKey: instance.key }),
                    )}
                </HorizBox>
            </HorizBox>
        </View>
    );
}

const InstanceCardStyle = StyleSheet.create({
    card: {
        padding: 10,
        borderRadius: 8,
        backgroundColor: colorSurfacePrimary,
        boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        maxWidth: 630,
    },
});

// FIXME: this needs a better type, but we are about to delete this module anyway.
export type InstanceManagerProps = { titleLabel: TLabel; instanceList: any[]; structureKey: string };

export function InstanceManager({ titleLabel, instanceList, structureKey }: InstanceManagerProps) {
    const s = InstanceManagerStyle;
    const datastore = useDatastore();
    const { instanceFilters = [] } = useConfig<InstanceManagerConfig>();

    const filteredInstanceList = instanceList.filter((instance) => instanceFilters.every((filter) => filter.filter(datastore, instance)));

    function onAdd() {
        datastore.pushSubscreen('newInstance', { structureKey });
    }

    return (
        <View style={s.container}>
            <WindowTitle label={titleLabel} />
            <HorizBox spread>
                <Heading label={titleLabel} type='large' />
                <CTAButton label={computedLabel('New ' + structureKey)} onPress={onAdd} size='compact' />
            </HorizBox>
            <Pad size={40} />
            <VertBox gap={16} wide>
                {instanceFilters.map((filter, index) => React.createElement(filter.component, { structureKey, instanceList, key: index }))}
            </VertBox>
            <Pad size={32} />
            <VertBox gap={16} wide>
                {filteredInstanceList.map((instance) => (
                    <Catcher key={instance.key}>
                        <InstanceCard instance={instance} structureKey={structureKey} />
                    </Catcher>
                ))}
            </VertBox>
        </View>
    );
}
const InstanceManagerStyle = StyleSheet.create({
    container: {
        maxWidth: 630,
    },
    searchField: {
        width: 200,
    },
});

export function QuestionManager() {
    const questionList = useGlobalQuestions();
    const instanceList = questionList.map((question) => ({
        key: question.key,
        name: question.question,
    }));

    return <InstanceManager instanceList={instanceList} structureKey='question' titleLabel={t('Questions')} />;
}

export function TopicManager() {
    const topicList = useGlobalTopics();
    return <InstanceManager instanceList={topicList} structureKey='topic' titleLabel={t('Topics')} />;
}

export type NewInstanceScreenProps = { structureKey: string };

export function NewInstanceScreen({ structureKey }: NewInstanceScreenProps) {
    const s = NewInstanceScreenStyle;
    const {
        newInstanceWidgets = [],
        newInstanceCreateBlockers = [],
        newInstanceCreators = [],
        newInstancePostCreateTriggers = [],
    } = useConfig<InstanceManagerConfig>();
    const [newInstance, setNewInstance] = useState({ name: '' });
    const datastore = useDatastore();

    const createBlocked = newInstanceCreateBlockers.some((blocker) => blocker(datastore, newInstance));

    async function onCreate() {
        const creator = newInstanceCreators.find((creator) => creator.structureKey === structureKey);
        if (creator) {
            const { key } = await creator.creator(datastore, newInstance);
            for (const trigger of newInstancePostCreateTriggers) {
                await trigger({
                    datastore: datastore,
                    instance: newInstance,
                    structureKey,
                    instanceKey: key,
                });
            }
            datastore.gotoInstance({ structureKey, instanceKey: key, replace: true });
        }
    }

    return (
        <View style={s.container}>
            <View style={s.helpColumn}></View>
            <View style={s.mainColumn}>
                <ScrollView>
                    <WindowTitle label={computedLabel('New ' + structureKey)} />
                    <Heading label={computedLabel('New ' + structureKey)} type='large' />
                    <Pad size={32} />
                    <FormField label={computedLabel(toTitleCase(structureKey))} required>
                        <TextField
                            multiline={false}
                            onChange={(name) => setNewInstance({ ...newInstance, name })}
                            placeholderLabel={computedLabel('Write your ' + structureKey + '...')}
                            testID='name'
                            value={newInstance.name}
                        />
                    </FormField>
                    {newInstanceWidgets.map((widget, index) =>
                        React.createElement(widget, { structureKey, key: index, instance: newInstance, setInstance: setNewInstance }),
                    )}
                    <Pad size={32} />
                    <CTAButton disabled={createBlocked} label={computedLabel('Create ' + structureKey)} onPress={onCreate} />
                    <Pad size={32} />
                </ScrollView>
            </View>
        </View>
    );
}
const NewInstanceScreenStyle = StyleSheet.create({
    container: {
        margin: 32,
        flexDirection: 'row',
        justifyContent: 'space-around',
    },
    helpColumn: {
        maxWidth: 300,
        flex: 1,
    },
    mainColumn: {
        flex: 3,
        maxWidth: 630,
    },
});
